import React, { FC } from 'react';

import { css, styled } from '@mui/material';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import CustomAccordion from '~/components/CustomAccordion/CustomAccordion';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import FilterItem from '~/pages/DiscoverPage/components/FilterItem/FilterItem';
import ShowMoreBtn from '~/pages/DiscoverPage/components/RefinementList/ShowMoreBtn';
import SearchInput from '~/pages/DiscoverPage/components/SearchInput/SearchInput';
import { SKELETON_TEXT_HEIGHT_SMALL_PX, SMALL_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';

interface RefinementListViewProps {
  showMore?: boolean;
  attribute: string;
  searchable?: boolean;
  searchForItems?: (val: string) => void;
  isFromSearch?: boolean;
  loading?: boolean;
  loadingItems?: number;
  delay?: number;
  items: any[];
  itemsToShowCount?: number;
  refine: (value: string) => void;
  translations?: {
    [key: string]: string;
  };
}

const RefinementListView: FC<RefinementListViewProps> = ({
  searchable,
  searchForItems,
  isFromSearch = false,
  loading,
  loadingItems,
  items,
  itemsToShowCount = 5,
  showMore,
  delay,
  refine,
  attribute,
  translations,
}) => {
  const { t } = useTenantTranslation();

  return (
    <>
      {searchable && searchForItems && (
        <SearchInput handleSearch={searchForItems} isFromSearch={isFromSearch} />
      )}
      {loading ? (
        new Array(loadingItems)
          .fill(null)
          .map((item, i) => <LoadingFilterOptions key={i} xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />)
      ) : items && items.length > 0 ? (
        showMore ? (
          <CustomAccordion
            itemsToShow={itemsToShowCount}
            ButtonComponent={ShowMoreBtn}
            delay={delay}
            wrapperCss={itemsWrapper}
          >
            {items.map((item) => (
              <FilterItem
                key={item.label}
                label={translations?.[item.value] || item.label}
                value={item.value}
                refine={refine}
                isRefined={item.isRefined}
                count={item.count}
                filterValue={attribute}
              />
            ))}
          </CustomAccordion>
        ) : (
          items.map((item) => (
            <FilterItem
              key={item.label}
              label={translations?.[item.value] || item.label}
              value={item.value}
              refine={refine}
              isRefined={item.isRefined}
              count={item.count}
              filterValue={attribute}
            />
          ))
        )
      ) : (
        <NoItems>{t('page.discover.noFiltersAvailable')}</NoItems>
      )}
    </>
  );
};

export default RefinementListView;

const LoadingFilterOptions = styled(AnimatedSkeleton, {
  shouldForwardProp: (prop) => prop !== 'wrapperWidth',
})`
  margin-bottom: ${SMALL_MARGIN_PX};

  &:last-child {
    margin-bottom: 0;
  }
`;
const NoItems = styled('div')`
  color: ${({ theme }) => theme.palette.common.gray};
  font-size: 16px;
`;
const itemsWrapper = css`
  overflow-x: visible;
  overflow-y: clip;
  padding-top: ${TINY_MARGIN_PX};
  transform: translateY(-${TINY_MARGIN_PX});
`;
